import { customBookFaqs } from './customBookFaqs';

export type FAQItem = {
  anchorId: string;
  question: string;
  answer: string;
  detailAnswer: string;
  image: string;
  updatedDate: string;
  aliases?: string[];
  links?: { label: string; href: string }[];
};

// Home, the FAQ index and question pages share stable IDs, copy and artwork.
export const productFaqs: FAQItem[] = [
  {
    "question": "What is Fable?",
    "answer": "Fable turns ideas into illustrated stories with narration, character voices, music and video. Choose Fable Go for a guided, hands-off experience or Fable Gambit for more creative control.",
    "anchorId": "what-is-fable",
    "detailAnswer": "Fable is a storytelling platform for families, authors, educators and creators. Start with an idea, choose your audience and creative direction, and bring the story to life with illustrations, narration, character voices and music. You can also turn completed stories into videos or custom printed books.\n\nFable Go handles most of the creation process for you. Fable Gambit, available in beta on the Web for Premium subscribers, gives you a conversational workspace to develop and refine the story stage by stage. Both use your Fable account and tokens.",
    "links": [
      {
        "label": "Explore Fable Go",
        "href": "/faq/what-is-fable-go"
      },
      {
        "label": "Explore Fable Gambit",
        "href": "/faq/what-is-fable-gambit"
      }
    ],
    "image": require('../images/faq/sage.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "anchorId": "what-is-fable-go",
    "question": "What is Fable Go?",
    "answer": "Go is Fable’s familiar, hands-off story creator. Describe your idea, choose your options and let Fable handle most of the process.",
    "detailAnswer": "Fable Go is the new name for Fable’s familiar story-creation experience. It is designed for people who want to describe an idea, choose the creative options and let Fable bring the story together. You can keep using the workflow you already know.\n\nChoose your story type, audience, characters, art style and voices where offered, then review the token cost before starting. Go handles most of the steps that Gambit lets you direct individually. Your finished story appears in your library for you to read, listen to and share.\n\nGo is available on the Web and in the mobile app, although older app versions may still use the previous name. Pro and Premium both include Go; generation uses your Fable tokens.",
    "image": require('../images/faq/avatars/go-otter-v1.webp'),
    "updatedDate": "2026-10-27",
    "links": [
      {
        "label": "Create with Go",
        "href": "/create"
      },
      {
        "label": "How Gambit differs",
        "href": "/faq/what-is-fable-gambit"
      }
    ]
  },
  {
    "anchorId": "what-is-fable-gambit",
    "question": "What is Fable Gambit?",
    "answer": "Gambit is a conversational story workspace for refining your idea, script, voices and artwork stage by stage. Its Web beta is included with Premium.",
    "detailAnswer": "Fable Gambit gives you more control over how a story comes together. Start with an idea, discuss the direction, and refine the blueprint and script before moving into audio, cast and world, artwork and other production stages. You can revisit decisions and iterate as the project takes shape.\n\nUse it to work on dialogue, adjust the tone, shape a character or direct the visual and audio details. The model selector lets you use Auto select or choose from the available models and providers. Available tools and token costs can differ, so check pricing before generating or regenerating media. Conversation and tool use spend Fable tokens too.\n\nGambit is currently in beta on the Web and is included with Premium. Sign in with your existing Fable account and choose Gambit from the creation menu. Pro includes Go, but not Gambit. Ultra will also include Gambit when that plan becomes available.",
    "image": require('../images/faq/avatars/gambit-raccoon-v1.webp'),
    "updatedDate": "2026-10-27",
    "links": [
      {
        "label": "Open Gambit on the Web",
        "href": "/create/gambit"
      },
      {
        "label": "Compare plans",
        "href": "/payment"
      },
      {
        "label": "View token pricing",
        "href": "/usage?view=pricing"
      }
    ]
  },
  {
    "question": "What subscription plans does Fable offer?",
    "answer": "Pro includes 1,000 monthly tokens and Go. Premium includes 2,500 monthly tokens and Go plus Gambit. Ultra is coming soon with 6,000 monthly tokens.",
    "anchorId": "what-subscription-plans-does-fable-offer",
    "detailAnswer": "Fable Pro includes 1,000 subscription tokens each month, Fable Go and 15% off printed books. Fable Premium includes 2,500 tokens each month, Go, access to Gambit’s Web beta and 30% off printed books.\n\nBoth plans include all art styles, narrators, story and musical themes, voice design and cloning, video tools, downloads, supported TV casting and social publishing, plus an ad-free experience. Creation and regeneration still use tokens; access to a tool does not mean unlimited generation.\n\nWeb prices are $17.99 per month or $197.89 per year for Pro, and $35.99 per month or $395.89 per year for Premium, in USD before applicable taxes. Annual plans cost the equivalent of 11 monthly payments and allocate tokens monthly. App Store and Google Play prices differ; checkout shows your local price and available billing periods.\n\nUltra is coming soon with 6,000 monthly tokens, Go and Gambit, and 40% off printed books. It is not currently available to purchase on the Web. Existing subscribers should check their account and billing-provider notices for their current allowance, price and any scheduled changes.",
    "links": [
      {
        "label": "See current plans and availability",
        "href": "/payment"
      },
      {
        "label": "Manage your subscription",
        "href": "/manage-subscription"
      }
    ],
    "image": require('../images/faq/rhino.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "anchorId": "how-do-subscription-and-top-up-tokens-work",
    "question": "How do subscription and top-up tokens work?",
    "answer": "Subscription tokens are spent first and refresh each month. Purchased tokens, welcome tokens and rewards go into your top-up balance, which does not expire.",
    "detailAnswer": "Your Fable balance has two parts. Subscription tokens come from your plan’s monthly allowance. Top-up tokens include purchased packs, signup tokens and eligible rewards. Fable uses subscription tokens first, then uses top-up tokens if the action costs more than your remaining subscription balance.\n\nUnused subscription tokens expire at the end of their allocation period. Top-up tokens do not expire, and a monthly refill does not replace or reduce them. For example, with 20 subscription tokens and 100 top-up tokens, a 50-token action uses the 20 subscription tokens and 30 top-up tokens, leaving 70 top-up tokens.\n\nOpen Usage to check both balances, activity and current token pricing. A token refund follows the original charge: the top-up portion returns to top-ups, while the subscription portion can return only while its original allocation is still active. Expired subscription tokens are not converted into permanent top-ups.",
    "image": require('../images/faq/avatars/tokens-squirrel-v1.webp'),
    "updatedDate": "2026-10-27",
    "links": [
      {
        "label": "View your usage",
        "href": "/usage"
      },
      {
        "label": "When monthly tokens refresh",
        "href": "/faq/when-do-subscription-tokens-refresh"
      },
      {
        "label": "Explore token packs",
        "href": "/payment"
      }
    ]
  },
  {
    "anchorId": "when-do-subscription-tokens-refresh",
    "question": "When do subscription tokens refresh?",
    "answer": "Monthly allowances follow your subscription’s allocation dates, including on annual plans. Unused subscription tokens do not roll over; top-ups stay available.",
    "detailAnswer": "Subscription tokens are allocated monthly while the subscription is eligible. Monthly billing and annual billing both provide monthly token allowances; paying annually does not grant the whole year’s tokens at once.\n\nThe allocation schedule follows your subscription dates rather than the first day of the calendar month. Check Usage for the period and remaining allowance shown for your account. An annual payment renewal and a monthly token refresh are different events.\n\nUnused subscription tokens expire at the allocation boundary. Purchased and reward tokens remain in your top-up balance. A trial or an existing subscription transitioning to a new allowance may show a different current allocation; check your account and provider notices rather than assuming the advertised allowance has already been added.",
    "image": require('../images/faq/avatars/refills-hedgehog-v1.webp'),
    "updatedDate": "2026-10-27",
    "links": [
      {
        "label": "Check your current allocation",
        "href": "/usage"
      },
      {
        "label": "Manage your subscription",
        "href": "/manage-subscription"
      }
    ]
  },
  {
    "question": "What makes Fable different from other storytelling apps?",
    "answer": "Bring writing, reusable characters, illustrations, narration, music and video into one story workflow. Go automates the process; Gambit lets you direct each stage.",
    "anchorId": "what-makes-fable-different",
    "detailAnswer": "Fable brings the parts of a story together in one place: the idea, cast, script, illustration style, voices, music and finished presentation. You can reuse characters, continue a story with another chapter, and build a collection to read, share or print.\n\nChoose how involved you want to be. Go is designed for describing a story and letting Fable assemble it. Gambit lets you discuss ideas, review each stage and refine individual elements. AI results can vary, so reviewing and adjusting the output remains part of the creative process.",
    "links": [],
    "image": require('../images/faq/onyx.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How does Fable work?",
    "answer": "Describe your idea, choose your audience and creative options, then create with Go or develop it stage by stage in Gambit. Photo-based characters are optional.",
    "anchorId": "how-does-fable-work",
    "detailAnswer": "Start with the story you want to tell. Choose an audience, language, story type, visual style and voices where available. You can add existing characters, create original ones or use photos for a lookalike character. Uploading photos is optional.\n\nIn Fable Go, review your choices and the token cost before starting generation. Fable puts the story together for you, and you can open the result from your library. Generation time depends on the story and media you request; longer stories, audio and video can take more time.\n\nFor a more involved process, use Fable Gambit on the Web. Discuss the idea, refine the blueprint and script, and review the audio, cast and artwork as your story takes shape.",
    "links": [
      {
        "label": "Start a story",
        "href": "/create"
      },
      {
        "label": "Go and Gambit explained",
        "href": "/faq/what-is-fable-gambit"
      }
    ],
    "image": require('../images/faq/nova.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Is Fable safe for children?",
    "answer": "Choose an appropriate reader age and review generated stories before sharing them with children. Fable uses safety controls, but AI output still needs adult judgment.",
    "anchorId": "is-fable-safe-for-children",
    "detailAnswer": "Fable supports stories for different audiences, including children. Set the intended reader age and describe the tone and themes you want, such as a gentle bedtime adventure. These choices help guide the story, language and imagery.\n\nSafety controls help limit unsuitable content, but they cannot guarantee every generated word, image or audio segment is appropriate for every child. A parent, guardian or educator should review the finished story before sharing it with a younger audience. Report any concerning result through Support.",
    "links": [
      {
        "label": "Contact support",
        "href": "/support"
      }
    ],
    "image": require('../images/faq/fable.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What age group is Fable designed for?",
    "answer": "Fable supports children, teens and adults. Choose the intended reader age, tone and subject to guide the story.",
    "anchorId": "what-age-group-is-fable-designed-for",
    "detailAnswer": "Fable is used for family stories, classroom activities, fantasy adventures, romance, mysteries and other creative projects. It is not limited to bedtime stories or a single age group.\n\nSet the intended audience when creating a story and describe any preferences for vocabulary, complexity or tone. For younger readers, review the finished result with an adult before sharing. An age setting guides generation; it is not a guarantee of suitability.",
    "links": [],
    "image": require('../images/faq/echo.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can I create multiple characters?",
    "answer": "Yes. Build a cast of original or photo-based characters and reuse them across stories. Character creation uses tokens where a cost is shown.",
    "anchorId": "can-i-create-multiple-characters",
    "detailAnswer": "You can create and save multiple characters, including original people, animals and imaginary creatures. Where supported, you can also use photos to create a lookalike character. There are no subscription character-slot allowances to manage.\n\nSelect the characters you want when creating a story, or build your cast in Gambit. Saved character references help carry their appearance into new scenes, but generated images may need review and refinement. Check the displayed token cost before creating or regenerating character artwork.",
    "links": [],
    "image": require('../images/faq/coral.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What types of stories can Fable create?",
    "answer": "Create adventures, fantasy, mysteries, bedtime tales, educational stories and more, with narration, character dialogue or musical storytelling.",
    "anchorId": "what-types-of-stories-can-fable-create",
    "detailAnswer": "Describe the genre, setting, audience and mood you have in mind. Fable can help with a gentle family tale, a science-fiction adventure, a mystery, an educational scenario or an original fictional world.\n\nChoose traditional narration, character dialogue or musical storytelling where available. In Gambit you can develop the idea conversationally and refine the script, voices and artwork. Each creative choice affects the finished story, and media generation uses tokens.",
    "links": [],
    "image": require('../images/faq/charls.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How long are the stories?",
    "answer": "Story length varies with your selected options, script and audio. Continue with new chapters when you want a longer adventure.",
    "anchorId": "how-long-are-the-stories",
    "detailAnswer": "Fable stories do not all have a fixed one-minute duration. The available length options, amount of dialogue, narration and music determine how much reading or listening time the finished story contains.\n\nChoose the length offered in the creation flow, or develop and refine a script in Gambit. You can continue a story with another chapter rather than fitting the whole adventure into one short story. Longer scripts and additional media can use more tokens.",
    "links": [],
    "image": require('../images/faq/atlas.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How many stories can I create?",
    "answer": "It depends on your token balance and the tools you choose. Check the cost before creating; subscriptions add a monthly allowance and top-ups add extra tokens.",
    "anchorId": "how-many-stories-can-i-create",
    "detailAnswer": "Fable uses tokens rather than a fixed number of stories per month. Different story formats, lengths and generation tools can cost different amounts. Editing or generating additional artwork, audio or video can also use tokens.\n\nNew accounts receive welcome tokens, and eligible rewards can add more. Paid plans include monthly subscription tokens; token packs add a separate top-up balance. The Usage page shows your balances and recent activity.",
    "links": [
      {
        "label": "How token balances work",
        "href": "/faq/how-do-subscription-and-top-up-tokens-work"
      },
      {
        "label": "View usage and pricing",
        "href": "/usage"
      }
    ],
    "image": require('../images/faq/ash.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What devices is Fable available on?",
    "answer": "Use Fable on the Web, iPhone, iPad and Android. Gambit is currently available in beta on the Web.",
    "anchorId": "what-devices-is-fable-available-on",
    "detailAnswer": "Fable is available in a web browser and through its iOS and Android apps. Sign in with the same Fable account to access your saved stories and account benefits across supported devices.\n\nFeature availability can differ by platform and app version. Gambit is currently a Web beta for Premium subscribers, so use the browser workspace for its stage-by-stage creative tools. Mobile updates may arrive separately from Web changes.",
    "links": [
      {
        "label": "Create on the Web",
        "href": "/create"
      }
    ],
    "image": require('../images/faq/alloy.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Is Fable free to use?",
    "answer": "New accounts receive welcome tokens, and eligible rewards can add more. Paid plans provide monthly tokens and additional tools; top-up packs are also available.",
    "anchorId": "is-fable-free-to-use",
    "detailAnswer": "You can sign up and try Fable with the welcome tokens offered to new accounts. Your available balance and any eligible rewards appear in the app. Creating stories and media spends tokens, so the amount you can make depends on the tools and options you choose.\n\nPaid plans include a monthly token allowance and membership benefits. Top-up packs are one-time purchases that add tokens without starting a subscription. Some features, including Gambit, require an eligible plan.",
    "links": [
      {
        "label": "Compare subscription plans",
        "href": "/faq/what-subscription-plans-does-fable-offer"
      }
    ],
    "image": require('../images/faq/lemur.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can I save and share stories?",
    "answer": "Your completed stories are saved to your library. Share a story link, download supported formats or use the publishing options available for your account.",
    "anchorId": "can-i-save-and-share-stories",
    "detailAnswer": "Completed stories are saved in your Fable library so you can return to them with the same account. Open a story to see its available sharing, download and video options.\n\nSharing a link makes that story available to the people who receive it. Review personal details, photos and the finished content before sharing. Supported formats and connected social publishing options depend on the story, platform and account features.",
    "links": [],
    "image": require('../images/faq/giraffe.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can I get hard or softcover books of my stories?",
    "answer": "Yes. Print one story or combine several into a collection. Book Builder offers compatible sizes, bindings, covers and finishes for your finished page count.",
    "anchorId": "can-i-get-hard-or-softcover-books",
    "detailAnswer": "Open Book Builder and choose one completed story or several stories for a collection. You can adjust the size, page layout and cover, then preview the book before ordering.\n\nAvailable bindings include paperback, casewrap hardcover, linen hardcover, coil and saddle stitch. Not every combination fits every page count or size; Book Builder shows the options supported by the composed book. Enter your delivery address to see shipping availability and the final quote.",
    "links": [
      {
        "label": "Explore custom books",
        "href": "/custom-books"
      },
      {
        "label": "Compare book bindings",
        "href": "/faq/which-binding-types-are-available"
      }
    ],
    "image": require('../images/faq/shimmer.jpg'),
    "updatedDate": "2026-10-27",
    "aliases": [
      "can-i-print-my-fable-stories",
      "what-printed-book-options-does-fable-offer"
    ]
  },
  {
    "question": "What makes Fable's AI voices special?",
    "answer": "Choose expressive narrators or give characters distinct voices. Paid plans also include voice design and cloning tools, with token costs shown when applicable.",
    "anchorId": "what-makes-fables-ai-voices-special",
    "detailAnswer": "Different narrators suit different moods, from a gentle bedtime reading to a dramatic adventure. Dialogue stories can give individual characters their own voices, so the story unfolds through conversations as well as narration.\n\nWith the available voice tools you can design a voice or clone a voice you have permission to use. In Gambit, review and refine the audio direction alongside your script. Listen to generated audio and adjust pronunciation or performance when needed.",
    "links": [
      {
        "label": "Explore narrators",
        "href": "/narrators"
      }
    ],
    "image": require('../images/faq/clownfish.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Is my child's data safe?",
    "answer": "Only upload information and photos you are comfortable using in a story. Our Privacy Policy explains how Fable and its service providers handle your data.",
    "anchorId": "is-my-childs-data-safe",
    "detailAnswer": "Photos and story details can be personal, particularly when they relate to children. Use only material you have permission to upload, and avoid including information such as a child's address, school or other sensitive details in a story you plan to share.\n\nFable uses service providers to operate the app and generate content. The Privacy Policy describes the information collected, how it is used and the choices available to you. Contact Support if you have a question about your account, photos or deleting data.",
    "links": [
      {
        "label": "Read our Privacy Policy",
        "href": "/privacy"
      },
      {
        "label": "Contact support",
        "href": "/support"
      }
    ],
    "image": require('../images/faq/elephant.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How do I get support if I have issues?",
    "answer": "Use Support on the Web or the app’s help options. Include the story or order involved, what happened and any error message.",
    "anchorId": "how-do-i-get-support-if-i-have-issues",
    "detailAnswer": "Open Support and describe what you were trying to do, what happened instead and whether you are using Web, iOS or Android. A screenshot, story identifier or order reference can help us locate the issue.\n\nFor a purchase question, include the platform used for payment and the relevant receipt or transaction reference. Do not send passwords, full card details or verification codes. For a generation problem, check the story status and token activity before retrying.",
    "links": [
      {
        "label": "Get help",
        "href": "/support"
      }
    ],
    "image": require('../images/faq/kangaroo.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can Fable create musical stories?",
    "answer": "Yes. Musical stories combine storytelling with original songs, lyrics and character voices. Choose a musical direction and review the generation cost.",
    "anchorId": "can-fable-create-musical-stories",
    "detailAnswer": "Choose musical storytelling to build songs and lyrics into your story. Set a musical theme and describe the mood you want, whether that is a gentle lullaby, a lively adventure or a theatrical scene.\n\nFable generates the musical content as part of the creative workflow. In Gambit, you can refine the script and audio direction as you work. Music generation can take additional time and tokens; review the result before sharing.",
    "links": [
      {
        "label": "Explore musical stories",
        "href": "/musicals"
      }
    ],
    "image": require('../images/faq/ghost.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What is Dialog Mode in Fable?",
    "answer": "Dialogue storytelling gives characters their own voices and lets scenes unfold through conversations, alongside narration where needed.",
    "anchorId": "what-is-dialog-mode",
    "detailAnswer": "In a dialogue story, characters speak to each other rather than having every line delivered by a single narrator. Different voices help distinguish the cast and can make a scene feel like an audio drama.\n\nChoose the dialogue option where offered in the creation flow, or develop dialogue in Gambit’s script stage. Review the character assignments and generated audio to make sure the voices, timing and pronunciation fit your story.",
    "links": [],
    "image": require('../images/faq/turtle.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What makes Fable the best AI app for children's storybook narrations?",
    "answer": "Fable combines illustrated stories with selectable narrators and character voices. Preview voices and choose the tone that fits your audience.",
    "anchorId": "best-ai-tool-for-childrens-storybook-narration",
    "detailAnswer": "Fable brings narration into the same workflow as the story and its illustrations. You can choose an expressive narrator, use separate voices for dialogue, and add music where it suits the story.\n\nFor children’s stories, listen to voice previews and select a pace and tone that suit the intended reader. Review the finished audio, including names and pronunciation, before sharing it. The best choice depends on your story and audience rather than a single narrator being right for everyone.",
    "links": [
      {
        "label": "Listen to narrators",
        "href": "/narrators"
      }
    ],
    "image": require('../images/faq/rex.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What languages does Fable support?",
    "answer": "Fable supports multiple story languages, including English, Spanish, French, German, Japanese and more. Available voices and features can vary by language.",
    "anchorId": "what-languages-does-fable-support",
    "detailAnswer": "Select the language offered in your creation screen to guide the story and its audio. Fable supports a range of languages for different readers and listeners; the Languages page lists the current collection.\n\nVoice choices and generation quality can differ by language and model. Preview the available voices and review the finished script and narration, especially names, accents and pronunciation. The interface language and the language you choose for a story are separate choices.",
    "links": [
      {
        "label": "Browse supported languages",
        "href": "/languages"
      }
    ],
    "image": require('../images/faq/monkey.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "\"An error occurred during sign in. Please try again.\"",
    "answer": "Check whether you are already signed in, refresh the page and try your usual sign-in method. Contact Support if the error continues.",
    "anchorId": "login-error-sign-in",
    "detailAnswer": "An interrupted sign-in or an expired browser session can cause a sign-in error. Return to the home page and check whether your account menu is already available. If not, refresh and try the same sign-in method you normally use for that Fable account.\n\nYou can also try a private browser window to check whether saved site data is involved. If necessary, clear only Fable’s site data and sign in again; this will sign you out in that browser. If the problem continues, send Support the error message and your browser or app version. Never share your password or verification code.",
    "links": [
      {
        "label": "Contact support",
        "href": "/support"
      }
    ],
    "image": require('../images/faq/clam.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How to create a story using artificial intelligence?",
    "answer": "Start with an idea, choose your creative options and create with Go, or refine the story conversationally in Gambit. Photos are optional.",
    "anchorId": "how-to-create-story-using-ai",
    "detailAnswer": "Describe the main character, setting and what should happen. You can be brief, such as “an otter who discovers a floating library,” or specify the tone, audience and details you want to keep. Add saved or original characters if they are part of your idea.\n\nFable Go guides you through the available story, voice and art options and shows the token cost before generation. Fable Gambit gives you more control through a conversational workflow with stages for planning, writing and producing your media. Review the result and refine it before sharing.",
    "links": [
      {
        "label": "Start creating",
        "href": "/create"
      },
      {
        "label": "Learn about Gambit",
        "href": "/faq/what-is-fable-gambit"
      }
    ],
    "image": require('../images/faq/raccoon.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How does AI personalization work in Fable stories?",
    "answer": "Personalize the cast, setting, audience, tone and visual style. Optional photo-based characters help bring familiar faces into your story.",
    "anchorId": "how-does-ai-personalization-work",
    "detailAnswer": "Personalization starts with your idea: the people or creatures involved, their personalities, where the story takes place and what matters to them. Set the reader age and style, and include the details you want Fable to use.\n\nYou can reuse saved characters or create lookalike characters from photos you have permission to upload. Character references help guide illustrations across scenes, although AI can still produce differences. Review the artwork and refine any details that do not match your creative direction.",
    "links": [],
    "image": require('../images/faq/buffalo.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How to make bedtime stories more engaging for kids?",
    "answer": "Use familiar characters, a gentle narrator and a calming theme. Keep the length suited to your child and review the story together.",
    "anchorId": "how-to-make-bedtime-stories-engaging",
    "detailAnswer": "Start with something familiar, such as a favorite animal, a place your child loves or a small challenge they understand. Describe a calm tone and a reassuring ending, and choose an appropriate reader age.\n\nA gentle narrator and a manageable length can help the story fit your routine. You can add a familiar character without needing to upload photos. Read or listen first, then enjoy the story together and talk about the parts your child finds interesting.",
    "links": [],
    "image": require('../images/faq/whale.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How does Fable's AI generate unique story illustrations?",
    "answer": "Fable uses the story, selected art style and character references to generate matching illustrations. Review and refine the artwork where needed.",
    "anchorId": "how-does-ai-generate-story-illustrations",
    "detailAnswer": "Fable turns descriptions of scenes and characters into illustrations using image-generation tools. Your chosen art style and saved character references guide the appearance, setting and mood.\n\nAI-generated details can vary between images. Review the cast, composition and scene details before sharing or printing. In Gambit, you can work through the visual direction and refine artwork as part of the story workflow. Generating another version can use additional tokens.",
    "links": [],
    "image": require('../images/faq/sloth.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How to use AI storytelling for educational purposes?",
    "answer": "Describe a topic, reader age and learning goal to create a story around it. Review factual details before using the result in a lesson.",
    "anchorId": "how-to-use-ai-storytelling-education",
    "detailAnswer": "Try a prompt that combines a concrete learning goal with an engaging situation, such as a garden adventure about pollination or a story about resolving a disagreement. Set the intended audience and specify the concepts or vocabulary you want to include.\n\nAI can make factual mistakes, so a teacher or parent should check explanations and examples before presenting the story as learning material. You can revise unclear parts, ask discussion questions and use the story as a starting point for a wider activity.",
    "links": [],
    "image": require('../images/faq/hedgehog.jpg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How does Fable ensure story quality and appropriateness?",
    "answer": "Creative settings and safety controls guide generation. Review the script, images and audio, then refine anything that needs attention.",
    "anchorId": "how-does-fable-ensure-story-quality",
    "detailAnswer": "Fable uses your audience, story description and creative settings to guide generation, alongside safety controls from the tools involved. Clear instructions and reusable character references help keep the story focused.\n\nAI can still produce factual errors, inconsistent artwork or unexpected audio. Review the completed story before sharing, teaching with it or ordering a printed copy. Gambit gives you stage-by-stage opportunities to revise the plan, script and media. Report a problematic result through Support.",
    "links": [
      {
        "label": "Get help with a story",
        "href": "/support"
      }
    ],
    "image": require('../images/faq/mouse.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can I download stories for offline viewing?",
    "answer": "Use the download options available on a completed story to save supported formats. Open the downloaded file once before relying on it offline.",
    "anchorId": "can-i-download-stories-offline",
    "detailAnswer": "Open a completed story and look for its download options. Depending on the available format and your plan, you can save a copy to your device for use outside Fable. If a video needs to be created first, that generation may take time and tokens.\n\nWait for the download to finish and check that it opens in a compatible viewer or player before going offline. Creating new stories, generating media and syncing changes still require an internet connection.",
    "links": [],
    "image": require('../images/faq/hippo.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can I cast Fable stories to my TV or other devices?",
    "answer": "Cast supported stories to a compatible TV or Google Cast device where the Cast control is available. Device, browser and network support can vary.",
    "anchorId": "can-i-cast-stories-to-tv",
    "detailAnswer": "Open a story and use its Cast control when it is available on your platform. Your phone or computer and the receiving TV or casting device generally need to be on the same network.\n\nCompatibility depends on the browser, app version and receiving device. If no receiver appears, check that the TV is ready to cast and that your devices share the same network. You can also use a supported downloaded video with your own player.",
    "links": [
      {
        "label": "Get casting help",
        "href": "/support"
      }
    ],
    "image": require('../images/faq/flamingo.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "Can I watch ads to earn Fable tokens?",
    "answer": "Rewarded video ads can add tokens where the app offers them. Availability and reward amounts may vary; check the offer before watching.",
    "anchorId": "can-i-watch-ads-to-earn-fable-tokens",
    "detailAnswer": "When a rewarded-video option is available in the app, it shows the reward associated with that offer. Complete the required viewing steps and allow time for the reward to be confirmed.\n\nAds are not available for every account, region or device at all times. Confirmed reward tokens go into your top-up balance and do not expire with a monthly subscription allocation. If a completed reward is missing, contact Support with the approximate time and device used.",
    "links": [
      {
        "label": "Understand your token balances",
        "href": "/faq/how-do-subscription-and-top-up-tokens-work"
      }
    ],
    "image": require('../images/faq/crab.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "How can I earn free Fable tokens?",
    "answer": "New-account welcome tokens, eligible rewards and promotional offers add to your top-up balance. Check the app for the offers available to you.",
    "anchorId": "how-can-i-earn-free-fable-tokens",
    "detailAnswer": "Fable offers welcome tokens to new accounts and may offer rewards or promotions, including rewarded video where available. The app shows the requirements and token amount for eligible offers.\n\nWelcome and reward tokens go into your top-up balance rather than your monthly subscription allowance. They do not disappear when the monthly allowance refreshes. Offers can change and may have eligibility requirements, so follow the details shown in the app.",
    "links": [
      {
        "label": "How token balances work",
        "href": "/faq/how-do-subscription-and-top-up-tokens-work"
      }
    ],
    "image": require('../images/faq/cheetah.jpeg'),
    "updatedDate": "2026-10-27"
  },
  {
    "question": "What devices can I watch Fable stories on?",
    "answer": "Read and listen on the Web, iOS or Android. Supported downloads and casting options let you enjoy completed stories on other compatible devices.",
    "anchorId": "what-devices-can-i-watch-fable-stories",
    "detailAnswer": "Sign in with the same Fable account on the Web or in the iOS or Android app to access your story library. The story viewer brings together the content already generated for that story, such as illustrations, narration and music.\n\nWhere supported, cast to a compatible receiver or download a finished format to play with another app. Playback features can vary by device and app version. Creating or updating stories and media requires an internet connection.",
    "links": [],
    "image": require('../images/faq/shimmer.jpg'),
    "updatedDate": "2026-10-27"
  }
];

export const faqData: FAQItem[] = [
  ...productFaqs,
  ...customBookFaqs.map(({ id, ...faq }) => ({
    ...faq, anchorId: id, updatedDate: '2026-10-27',
    links: [{ label: 'Explore custom books', href: '/custom-books' }],
  })),
];

const questionSlug = (question: string) => question.toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');

export const findFaq = (slug?: string): FAQItem | undefined => slug
  ? faqData.find(faq => faq.anchorId === slug || faq.aliases?.includes(slug)
    || questionSlug(faq.question) === slug)
  : undefined;

export const homepageFAQData = productFaqs.map(faq => ({ ...faq, preview: faq.answer }));
